import React from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, Route, Routes } from 'react-router-dom';
import FullPageLoading from './components/base/FullPageLoading';
import CompanyAdminSidebar from './components/companyAdmin/CompanyAdminSidebar';
import Layout from './components/layout/Layout';
import LoginLayout from './components/layout/LoginLayout';
import useCompany from './context/company/useCompany';
import ChangePassword from './pages/ChangePassword';
import CreateTask from './pages/CreateTask';
import Error404 from './pages/Error404';
import Favorites from './pages/Favorites';
import Login from './pages/Login';
import Logout from './pages/Logout';
import Modules from './pages/Modules';
import Task from './pages/Task';
import TaskDefinitionSettings from './pages/TaskDefinitionSettings';
import TaskDefinitions from './pages/TaskDefinitions';
import AdminDashboard from './pages/admin/AdminDashboard';
import Categories from './pages/admin/Categories';
import Companies from './pages/admin/Companies';
import GatewayConfigs from './pages/admin/GatewayConfigs';
import InstanceConfig from './pages/admin/InstanceConfig';
import { default as AdminModules } from './pages/admin/Modules';
import TaskDefinition from './pages/admin/TaskDefinition';
import { default as AdminTaskDefinitions } from './pages/admin/TaskDefinitions';
import Company from './pages/companyAdmin/Company';
import CompanyAdminDashboard from './pages/companyAdmin/CompanyAdminDashboard';
import KnowledgeBase from './pages/companyAdmin/KnowledgeBase';
import KnowledgeBases from './pages/companyAdmin/KnowledgeBases';
import Users from './pages/companyAdmin/Users';
import SidebarLayout from './components/sidebar/SidebarLayout';
import TaskDefinitionsSidebar from './components/taskDefinitions/TaskDefinitionsSidebar';
import TasksSidebar from './components/tasks/TasksSidebar';
import FavoritesSidebar from './components/favorites/FavoritesSidebar';
import AdminSidebar from './components/admin/AdminSidebar';

const App = () => {
  const {
    isLoading: authIsLoading,
    isAuthenticated,
    activeNavigator,
    error: authError,
  } = useAuth();
  const { isLoading } = useCompany();

  if (authIsLoading || isLoading) return <FullPageLoading />;

  switch (activeNavigator) {
    case 'signinSilent':
      return <FullPageLoading message="Anmelden..." />;
    case 'signoutRedirect':
      return <FullPageLoading message="Abmelden..." />;
  }

  if (authError) return <FullPageLoading message="Abmelden..." />;

  if (!isAuthenticated)
    return (
      <Routes>
        <Route element={<LoginLayout />}>
          <Route element={<Logout />} path="/logout" />
          <Route element={<Login />} path="*" />
        </Route>
      </Routes>
    );

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="" element={<Navigate to="/tasks" replace />} />

        <Route
          element={<SidebarLayout leftSideBar={<TaskDefinitionsSidebar />} />}
        >
          <Route element={<Modules />} path="/modules" />
          <Route
            element={<TaskDefinitions />}
            path="module/:moduleId/:categoryId?"
          />
          <Route
            element={<TaskDefinitionSettings />}
            path="module/:moduleId/:categoryId?/settings/:taskDefinitionId"
          />
          <Route
            element={<CreateTask />}
            path="module/:moduleId/:categoryId?/create/:taskDefinitionId"
          />
        </Route>

        <Route element={<SidebarLayout leftSideBar={<TasksSidebar />} />}>
          <Route element={<Task />} path="tasks" />
          <Route element={<Task />} path="task/:taskId" />
        </Route>

        <Route element={<SidebarLayout leftSideBar={<FavoritesSidebar />} />}>
          <Route element={<Favorites />} path="favorites/:type?" />
        </Route>

        <Route element={<ChangePassword />} path="changePassword" />

        <Route
          path="companyAdmin"
          element={<SidebarLayout leftSideBar={<CompanyAdminSidebar />} />}
        >
          <Route element={<CompanyAdminDashboard />} path="" />
          <Route element={<KnowledgeBases />} path="knowledgeBases" />
          <Route element={<KnowledgeBase />} path="knowledgeBase/:id" />
          <Route element={<Users />} path="users" />
          <Route element={<Company />} path="company" />
        </Route>

        <Route
          path="admin"
          element={<SidebarLayout leftSideBar={<AdminSidebar />} />}
        >
          <Route element={<AdminDashboard />} path="" />
          <Route element={<InstanceConfig />} path="instanceConfig" />
          <Route element={<GatewayConfigs />} path="providerConfigs" />
          <Route element={<Categories />} path="categories" />
          <Route element={<Companies />} path="companies" />
          <Route element={<AdminModules />} path="modules" />
          <Route element={<AdminTaskDefinitions />} path="taskDefinitions" />
          <Route element={<TaskDefinition />} path="taskDefinition/:id" />
          <Route element={<Error404 />} path="*" />
        </Route>
        <Route element={<Error404 />} path="*" />
      </Route>
    </Routes>
  );
};

export default App;
